import React, { useEffect, useState } from "react";
import { X, FileText, ArrowRight, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const SHOWN_KEY = "cwk_exit_popup_v1_shown";

export default function ExitIntentPopup() {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", mobile: "" });
  const [done, setDone] = useState(false);

  useEffect(() => {
    let shown = false;
    try { shown = localStorage.getItem(SHOWN_KEY) === "1"; } catch {/* ignore */}
    if (shown) return;
    // Trigger on mouseleave from the top (desktop) OR after 25s on page (fallback)
    const onLeave = (e) => {
      if (e.clientY <= 0 && !shown) {
        shown = true;
        setOpen(true);
        try { localStorage.setItem(SHOWN_KEY, "1"); } catch {/* */}
      }
    };
    const fallback = setTimeout(() => {
      if (!shown) { shown = true; setOpen(true); try { localStorage.setItem(SHOWN_KEY, "1"); } catch {/* */} }
    }, 30000);
    document.addEventListener("mouseleave", onLeave);
    return () => { document.removeEventListener("mouseleave", onLeave); clearTimeout(fallback); };
  }, []);

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please enter your name and email");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", {
        name: form.name, email: form.email, mobile: form.mobile,
        course_interest: "Free Digital Marketing Syllabus", source: "exit_popup",
      });
      setDone(true);
      toast.success("Free syllabus sent to your email!");
    } catch (err) {
      toast.error("Something went wrong — please try again");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-end md:items-center justify-center p-3 md:p-6 animate-in fade-in" data-testid="exit-popup">
      <div className="relative w-full max-w-md bg-[#0a0a0a] border border-[#FF0066]/30 rounded-2xl shadow-2xl shadow-[#FF0066]/30 overflow-hidden">
        <button type="button" onClick={() => setOpen(false)} className="absolute top-3 right-3 z-20 text-white/50 hover:text-white p-1 rounded-full bg-black/40 hover:bg-black/60" data-testid="exit-popup-close" aria-label="Close">
          <X className="w-5 h-5" />
        </button>
        <div className="absolute -top-20 -right-20 w-56 h-56 bg-[#FF0066]/20 rounded-full blur-[80px] pointer-events-none" />

        {!done ? (
          <form onSubmit={submit} className="relative p-6">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-400/15 border border-amber-400/40 text-amber-300 text-[10px] uppercase tracking-wider font-bold">
              <Sparkles className="w-3 h-3" /> Wait! Don't miss this
            </div>
            <h2 className="mt-3 text-2xl font-extrabold text-white leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Get the complete <span className="text-[#FF0066]">Digital Marketing Syllabus</span> — FREE
            </h2>
            <p className="mt-2 text-sm text-white/65">
              60-page PDF · curriculum · salary expectations · placement roadmap. Delivered to your inbox in 30 seconds.
            </p>
            <div className="mt-4 space-y-2.5">
              <input
                type="text" placeholder="Your name *" required value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="w-full bg-[#050505] border border-white/15 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#FF0066]"
                data-testid="exit-popup-name"
              />
              <input
                type="email" placeholder="Your email *" required value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className="w-full bg-[#050505] border border-white/15 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#FF0066]"
                data-testid="exit-popup-email"
              />
              <input
                type="tel" placeholder="Mobile (optional)" value={form.mobile}
                onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value }))}
                className="w-full bg-[#050505] border border-white/15 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#FF0066]"
                data-testid="exit-popup-mobile"
              />
            </div>
            <button
              type="submit" disabled={submitting}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#FF0066] hover:bg-[#e6005c] disabled:opacity-60 text-white font-semibold rounded-full px-4 py-3 text-sm transition shadow-[0_8px_30px_-8px_rgba(255,0,102,0.6)]"
              data-testid="exit-popup-submit"
            >
              {submitting ? "Sending…" : <><FileText className="w-4 h-4" /> Send me the free syllabus</>}
            </button>
            <p className="mt-2 text-[10px] text-white/40 text-center">No spam · You can unsubscribe anytime</p>
          </form>
        ) : (
          <div className="relative p-6 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center mx-auto">
              <FileText className="w-7 h-7 text-emerald-300" />
            </div>
            <h3 className="mt-4 text-xl font-bold text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Check your inbox!</h3>
            <p className="mt-2 text-sm text-white/65">Syllabus link sent to <b className="text-white">{form.email}</b>. Inbox + spam folder check kar lein.</p>
            <a href={`https://wa.me/919131341638?text=${encodeURIComponent(`Hi Sir, I just downloaded the syllabus. Mera naam ${form.name} hai.`)}`}
               target="_blank" rel="noreferrer"
               className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] text-white font-semibold rounded-full px-4 py-2.5 text-sm transition"
               data-testid="exit-popup-wa-cta">
              Continue on WhatsApp <ArrowRight className="w-4 h-4" />
            </a>
            <button onClick={() => setOpen(false)} className="mt-3 text-xs text-white/40 hover:text-white">Close</button>
          </div>
        )}
      </div>
    </div>
  );
}
